/**
 * English: Voice
 *
 * What this file covers: Phone numbers, calls, recordings and the phone assistant.
 *
 * Tone: Clear and reassuring. Recording and consent wording is legal in effect: translate it exactly.
 *
 * English: English is the reference: every key is added here first, in South African English (organise, colour,
 * cancelled). Every other language falls back to this text for any key it does not have yet.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const voice: Catalog = {
  'voice.title': 'Voice',
  'voice.description': 'Phone calls answered by your assistant, and passed to your team when a person is needed.',
  'voice.unavailable.title': 'Voice is not available',
  'voice.unavailable.body': 'Phone calls are not offered on your account at the moment.',
  'voice.state.applied': 'Waiting for approval',
  'voice.state.active': 'On',
  'voice.start.title': 'Let your assistant answer the phone',
  'voice.start.body': 'Callers are answered at once, at any hour, from the same knowledge your assistant already uses in chat.',
  'voice.start.point.answer': 'Your assistant answers calls to your number and looks up answers as it speaks.',
  'voice.start.point.transfer': 'When a caller needs a person, the call is passed to someone on your team or to a number you choose.',
  'voice.start.point.prepaid': 'Calls are paid for from your credits, by the minute. A call cannot start without enough credit, and you set the most that can be spent in a day.',
  'voice.start.point.privacy': 'What a caller says to the assistant is processed outside South Africa. You choose what callers are told, and whether calls are recorded.',
  'voice.start.accept': 'I accept the voice terms (version {{version}})',
  'voice.start.readTerms': 'Read the voice terms',
  'voice.start.go': 'Ask for voice',
  'voice.start.noTerms': 'Voice cannot be started yet. Its terms are still being prepared.',
  'voice.start.doneApplied': 'Thank you. We will let you know when voice is switched on for you.',
  'voice.start.doneActive': 'Voice is switched on for your account.',
  'voice.applied.title': 'You have asked for voice',
  'voice.applied.body': 'We are reviewing your request. Nothing is charged and no call is answered until it is switched on.',
  'voice.suspended.title': 'Voice is suspended on your account',
  'voice.suspended.noReason': 'No reason was recorded.',
  'voice.suspended.help': 'Calls are not answered while it is suspended. Contact support to have it looked at.',
  'voice.active.title': 'Voice is on',
  'voice.active.body': 'Set how much may be spent on calls and whether calls are recorded. You can lower these limits at any time.',
  'voice.active.outboundBlocked': 'Outgoing calls are blocked on your account. {{reason}}',
  'voice.limits.title': 'Limits',
  'voice.limits.body': 'Limits protect you if something goes wrong. When one is reached, outgoing calls stop until the next hour or day. One credit is R1.',
  'voice.limits.daily': 'Most to spend on calls in a day (credits)',
  'voice.limits.hourly': 'Most to spend on calls in an hour (credits)',
  'voice.limits.minutes': 'Longest call (minutes)',
  'voice.limits.calls': 'Calls at the same time',
  'voice.limits.upTo': 'Up to {{max}} credits on your account.',
  'voice.limits.upToMinutes': 'Up to {{max}} minutes on your account.',
  'voice.limits.upToCalls': 'Up to {{max}} on your account.',
  'voice.limits.internationalOff': 'Calls to other countries are switched off on your account.',
  'voice.limits.internationalOn': 'Calls to other countries are allowed on your account.',
  'voice.recording.title': 'Recording and privacy',
  'voice.recording.body': 'A written record of each call answered by the assistant is always kept, so your team can see what was said. Recording the sound is your choice.',
  'voice.recording.enable': 'Record calls',
  'voice.recording.enableHint': 'Callers are told at the start of every call. Recordings are stored securely and deleted after the period you set.',
  'voice.recording.notice': 'What callers are told',
  'voice.recording.noticeHint': 'Spoken at the start of every recorded call, by the assistant or as a recording both sides hear when a person answers or calls out. Keep it to one sentence.',
  'voice.recording.retention': 'Keep recordings for (days)',
  'voice.recording.retentionHint': 'After this many days a recording is deleted for good.',
  'voice.recording.unavailable': 'Recording the sound of calls is not available at the moment.',
  'voice.settings.saved': 'Voice settings saved',
  'voice.settings.billingLink': 'See your credits and what calls have cost',
  'voice.tabs.numbers': 'Numbers',
  'voice.tabs.calls': 'Calls',
  'voice.tabs.settings': 'Limits and recording',
  'voice.numbers.title': 'Your numbers',
  'voice.numbers.intro': 'Each number here is answered by your assistant. Add a number once its phone provider is connected below.',
  'voice.numbers.add': 'Add a number',
  'voice.numbers.empty': 'No numbers yet',
  'voice.numbers.emptyHint': 'Add the number your customers call. It is answered as soon as the assistant is ready.',
  'voice.numbers.emptyNoProvider': 'Connect your phone provider first, then add the number that comes through it.',
  'voice.numbers.status.active': 'On',
  'voice.numbers.status.pending': 'Paused',
  'voice.numbers.status.suspended': 'Suspended',
  'voice.numbers.assistant.ready': 'The assistant is ready to answer this number.',
  'voice.numbers.assistant.pending': 'The assistant is being set up for this number. This usually takes under a minute.',
  'voice.numbers.assistant.failed': 'The assistant could not be set up for this number yet. We keep trying.',
  'voice.numbers.assistant.paused': 'Calls to this number are not answered while it is paused.',
  'voice.numbers.pause': 'Pause',
  'voice.numbers.resume': 'Switch on',
  'voice.numbers.removeTitle': 'Remove this number?',
  'voice.numbers.removeBody': 'Calls to {{number}} will no longer be answered here. The number stays with your phone provider.',
  'voice.numbers.removed': 'Number removed.',
  'voice.numbers.saved': 'Number saved.',
  'voice.numbers.form.new': 'Add a number',
  'voice.numbers.form.edit': 'How this number is answered',
  'voice.numbers.form.number': 'Phone number',
  'voice.numbers.form.numberHint': 'The number your customers call, such as 087 123 4567 or +27 87 123 4567.',
  'voice.numbers.form.label': 'Name',
  'voice.numbers.form.labelHint': 'For your own reference, such as Front desk or Sales.',
  'voice.numbers.form.provider': 'Phone provider',
  'voice.numbers.form.providerHint': 'The provider this number comes through. Calls from anywhere else are not answered.',
  'voice.numbers.form.assistant': 'Assistant',
  'voice.numbers.form.assistantHint': 'Which assistant answers. It uses the same knowledge as in your chats.',
  'voice.numbers.form.assistantDefault': 'Your default assistant',
  'voice.numbers.form.greeting': 'Greeting',
  'voice.numbers.form.greetingHint': 'The first thing a caller hears. Keep it to one sentence.',
  'voice.numbers.form.greetingDefault': 'Hello, thank you for calling. How can I help you?',
  'voice.numbers.form.language': 'Language',
  'voice.numbers.form.languageHint': 'The language most callers speak. The assistant still answers in the caller\'s language when it can.',
  'voice.numbers.language.en': 'English',
  'voice.numbers.language.af': 'Afrikaans',
  'voice.numbers.language.zu': 'isiZulu',
  'voice.numbers.language.xh': 'isiXhosa',
  'voice.numbers.language.st': 'Sesotho',
  'voice.numbers.form.instructions': 'Anything else the assistant should know on calls',
  'voice.numbers.form.instructionsHint': 'For example: We close at one on Saturdays. Take a message for anything about accounts.',
  'voice.providers.title': 'Phone providers',
  'voice.providers.intro': 'The company your number is with. Connect it once and add as many of its numbers as you like.',
  'voice.providers.add': 'Connect a provider',
  'voice.providers.empty': 'No provider connected',
  'voice.providers.emptyHint': 'You need the server name your provider gave you, and either a username and password or the ability to forward calls to an address.',
  'voice.providers.status.new': 'Checking',
  'voice.providers.status.ok': 'Connected',
  'voice.providers.status.failing': 'Not connected',
  'voice.providers.modeLine.register': 'We sign in to {{host}}',
  'voice.providers.modeLine.forward': '{{host}} sends calls to us',
  'voice.providers.checked': 'checked {{when}}',
  'voice.providers.removeTitle': 'Disconnect this provider?',
  'voice.providers.removeBody': '{{name}} will be disconnected. Remove its numbers first if it still has any.',
  'voice.providers.removed': 'Provider disconnected.',
  'voice.providers.saved': 'Provider saved. We are checking the connection now.',
  'voice.providers.form.new': 'Connect a phone provider',
  'voice.providers.form.edit': 'Change a phone provider',
  'voice.providers.form.name': 'Name',
  'voice.providers.form.nameHint': 'For your own reference, such as the provider\'s name.',
  'voice.providers.form.mode': 'How it connects',
  'voice.providers.form.mode.register': 'We sign in',
  'voice.providers.form.mode.forward': 'It sends calls to us',
  'voice.providers.form.modeHint.register': 'Your provider gave you a server, a username and a password. This is the usual way.',
  'voice.providers.form.modeHint.forward': 'Your provider forwards calls to an address you give it. Use this when it has no username and password.',
  'voice.providers.form.host': 'Server',
  'voice.providers.form.hostHint': 'The server name or address from your provider, such as sip.example.co.za.',
  'voice.providers.form.port': 'Port',
  'voice.providers.form.portHint': '5060 unless your provider says otherwise.',
  'voice.providers.form.transport': 'Connection type',
  'voice.providers.form.transportHint': 'UDP unless your provider says otherwise.',
  'voice.providers.form.username': 'Username',
  'voice.providers.form.usernameHint': 'From your provider. It is often the account or the number itself.',
  'voice.providers.form.password': 'Password',
  'voice.providers.form.passwordHint': 'From your provider. It is stored encrypted and never shown again.',
  'voice.providers.form.passwordKeep': 'Leave empty to keep the password already saved.',
  'voice.providers.form.sendTo': 'Ask your provider to forward calls to this address, with your own number in place:',
  'voice.providers.form.sendToNumber': 'your-number',
  'voice.providers.form.sources': 'Addresses its calls come from',
  'voice.providers.form.sourcesHint': 'Only if your provider sends calls from addresses other than the server above. One per line, such as 196.10.20.30 or 196.10.20.0/24.',
  'voice.calls.title': 'Calls',
  'voice.calls.intro': 'Every call to your numbers, with what was said and what it cost.',
  'voice.calls.empty': 'No calls yet',
  'voice.calls.emptyHint': 'Calls appear here as they happen.',
  'voice.calls.unknownCaller': 'Unknown caller',
  'voice.calls.line': '{{when}} to {{number}}',
  'voice.calls.noCharge': 'No charge',
  'voice.calls.length.none': 'Not answered',
  'voice.calls.length.minutes': '{{minutes}} min',
  'voice.calls.length.minutesSeconds': '{{minutes}} min {{seconds}} s',
  'voice.calls.status.ringing': 'Ringing',
  'voice.calls.status.active': 'In progress',
  'voice.calls.status.ended': 'Ended',
  'voice.calls.ended.completed': 'Completed',
  'voice.calls.ended.caller_hung_up': 'Caller hung up',
  'voice.calls.ended.no_credit': 'Credits ran out',
  'voice.calls.ended.max_length': 'Reached the longest call length',
  'voice.calls.ended.kill_switch': 'Ended by the service',
  'voice.calls.ended.engine_lost': 'Connection lost',
  'voice.calls.ended.failed': 'Could not be answered',
  'voice.calls.ended.ended_by_platform': 'Ended by support',
  'voice.calls.showing': '{{from}} to {{to}} of {{total}}',
  'voice.calls.newer': 'Newer',
  'voice.calls.older': 'Older',
  'voice.calls.detail.title': 'Call',
  'voice.calls.detail.from': 'From',
  'voice.calls.detail.to': 'To',
  'voice.calls.detail.when': 'When',
  'voice.calls.detail.ended': 'How it ended',
  'voice.calls.detail.length': 'Charged for',
  'voice.calls.detail.cost': 'Cost',
  'voice.calls.detail.costHint': 'Calls are charged by the whole minute. A call that could not be answered costs nothing.',
  'voice.calls.detail.transcript': 'What was said',
  'voice.calls.detail.noTranscript': 'Nothing was said on this call.',
  'voice.calls.speaker.caller': 'Caller',
  'voice.calls.speaker.assistant': 'Assistant',
  'voice.calls.speaker.tool': 'Assistant did',
  'voice.calls.speaker.system': 'Note',
  'voice.tabs.team': 'Team',
  'voice.calls.passedTo': 'Passed to {{who}}',
  'voice.calls.pass.label': 'Pass this call to',
  'voice.calls.pass.hint': 'They ring while the caller stays with the assistant. If nobody answers, nothing changes.',
  'voice.calls.pass.choose': 'Choose a person or department',
  'voice.calls.pass.go': 'Pass the call',
  'voice.calls.pass.done': 'The call is now with {{who}}.',
  'voice.calls.ended.no_answer': 'No answer',
  'voice.lines.title': 'Lines',
  'voice.lines.intro': 'A line is a person who takes calls: on a phone or app that signs in, or on a mobile or landline the call is forwarded to. Colleagues dial each other by the line\'s number.',
  'voice.lines.add': 'Add a line',
  'voice.lines.empty': 'No lines yet',
  'voice.lines.emptyHint': 'Add a line for each person the assistant should be able to pass a caller to.',
  'voice.lines.state.signedIn': 'Signed in',
  'voice.lines.state.signedOut': 'Not signed in',
  'voice.lines.state.forward': 'Forward',
  'voice.lines.state.off': 'Off',
  'voice.lines.state.dialOut': 'Can call out',
  'voice.lines.forwardLine': 'Calls are forwarded to {{number}}',
  'voice.lines.phoneLineIn': 'A phone is signed in and can take calls.',
  'voice.lines.phoneLineOut': 'No phone is signed in, so this line cannot ring.',
  'voice.lines.noTransfers': 'the assistant does not pass callers here',
  'voice.lines.replace': 'New sign-in details',
  'voice.lines.replaceTitle': 'Replace the sign-in details?',
  'voice.lines.replaceBody': 'The phone signed in as {{name}} is signed out at once and will need the new details. Use this when a phone is lost or a person leaves.',
  'voice.lines.replaceConfirm': 'Replace them',
  'voice.lines.removeTitle': 'Remove this line?',
  'voice.lines.removeBody': '{{name}} ({{number}}) will no longer take calls, and its phone is signed out.',
  'voice.lines.removed': 'Line removed.',
  'voice.lines.saved': 'Line saved.',
  'voice.lines.blocked.title': 'Outgoing calls have been stopped',
  'voice.lines.blocked.help': 'Incoming calls still work. Contact support to have outgoing calls switched back on.',
  'voice.lines.form.new': 'Add a line',
  'voice.lines.form.edit': 'Change a line',
  'voice.lines.form.kind': 'What takes the call',
  'voice.lines.form.kind.phone': 'A phone or app',
  'voice.lines.form.kind.forward': 'A mobile or landline',
  'voice.lines.form.kindHint.phone': 'A desk phone or an app that signs in with details we give you. Nothing extra is charged for its side of a call.',
  'voice.lines.form.kindHint.forward': 'The call is forwarded to an outside number through your phone provider, and charged as a second line.',
  'voice.lines.form.number': 'Line number',
  'voice.lines.form.numberHint': 'Three or four digits, such as 101.',
  'voice.lines.form.name': 'Name',
  'voice.lines.form.nameHint': 'The person\'s name. The assistant uses it when a caller asks for them.',
  'voice.lines.form.forwardTo': 'Forward to',
  'voice.lines.form.forwardToHint': 'A South African mobile or landline number, such as 082 555 0100.',
  'voice.lines.form.transfers': 'The assistant may pass callers to this line',
  'voice.lines.form.transfersHint': 'Switch off for a line that should only be reached through a department.',
  'voice.lines.form.dialOut': 'This line may call outside numbers',
  'voice.lines.form.dialOutHint': 'Off unless you switch it on. Calls go out through your phone provider and show your number.',
  'voice.lines.form.dialOutUnavailable': 'Outgoing calls are not available at the moment.',
  'voice.lines.form.active': 'Line is on',
  'voice.lines.form.activeHint': 'Switch off to stop this line ringing without removing it.',
  'voice.lines.details.title': 'Sign-in details for this phone',
  'voice.lines.details.once': 'Copy these now. The password is shown this once and cannot be shown again; if it is lost, choose New sign-in details.',
  'voice.lines.details.server': 'Server',
  'voice.lines.details.serverLater': 'Support will give you the server address',
  'voice.lines.details.port': 'Port',
  'voice.lines.details.transport': 'Connection type',
  'voice.lines.details.sound': 'Sound',
  'voice.lines.details.soundValue': 'Encrypted (SRTP), required',
  'voice.lines.details.username': 'Username',
  'voice.lines.details.password': 'Password',
  'voice.lines.details.help': 'Enter these in the phone or app as a SIP account. It must use TLS and encrypted sound, or it will not connect.',
  'voice.lines.details.copy': 'Copy',
  'voice.lines.details.copied': 'Copied.',
  'voice.lines.details.done': 'I have saved them',
  'voice.groups.title': 'Departments',
  'voice.groups.intro': 'A department is several lines that ring for the same reason, such as Sales. The assistant passes a caller to a department by its name.',
  'voice.groups.add': 'Add a department',
  'voice.groups.empty': 'No departments yet',
  'voice.groups.emptyHint': 'Add a department when more than one person can take the same kind of call.',
  'voice.groups.noLines': 'No lines yet, so the assistant cannot pass a caller here.',
  'voice.groups.strategyLine.together': 'All ring at once for {{seconds}} seconds',
  'voice.groups.strategyLine.in_order': 'Ring one after the other, {{seconds}} seconds in all',
  'voice.groups.purposeLine': 'For: {{purpose}}',
  'voice.groups.removeTitle': 'Remove this department?',
  'voice.groups.removeBody': '{{name}} will be removed. Its lines stay.',
  'voice.groups.removed': 'Department removed.',
  'voice.groups.saved': 'Department saved.',
  'voice.groups.form.new': 'Add a department',
  'voice.groups.form.edit': 'Change a department',
  'voice.groups.form.name': 'Name',
  'voice.groups.form.nameHint': 'What a caller would ask for, such as Sales or Accounts.',
  'voice.groups.form.purpose': 'What it is for',
  'voice.groups.form.purposeHint': 'Tells the assistant when to pass a caller here, such as: new orders and quotes.',
  'voice.groups.form.strategy': 'How the lines ring',
  'voice.groups.form.strategy.together': 'All at once',
  'voice.groups.form.strategy.in_order': 'One after the other',
  'voice.groups.form.strategyHint.together': 'Every line rings; the first to answer takes the call.',
  'voice.groups.form.strategyHint.in_order': 'Each line rings in turn, in the order you tick them.',
  'voice.groups.form.seconds': 'Ring for (seconds)',
  'voice.groups.form.secondsHint': 'Between 5 and 120. After this the caller goes back to the assistant.',
  'voice.groups.form.lines': 'Lines in this department',
  'voice.groups.form.linesHint': 'A phone that is not signed in is skipped.',
  'voice.numbers.form.answerWith': 'Who answers first',
  'voice.numbers.form.answerWith.assistant': 'The assistant',
  'voice.numbers.form.answerWith.department': 'A department',
  'voice.numbers.form.answerWithHint.assistant': 'The assistant answers and passes the caller on when a person is needed.',
  'voice.numbers.form.answerWithHint.department': 'The department rings first. If nobody answers, the assistant does.',
  'voice.numbers.form.department': 'Department',
  'voice.numbers.form.departmentHint': 'The department that rings first.',
  'voice.numbers.form.departmentChoose': 'Choose a department',
  'voice.numbers.fromUs': 'From us',
  'voice.numbers.rental.next': 'Next rental on {{when}}',
  'voice.numbers.rental.overdue': 'The rental for this number has not been paid. Top up to keep it.',
  'voice.apply.title': 'Get a number from us',
  'voice.apply.intro': 'No phone number of your own? Apply for one. We check your details, assign a number, and your assistant answers it.',
  'voice.apply.go': 'Apply for a number',
  'voice.apply.cost.setup': 'Registration, once',
  'voice.apply.cost.monthly': 'Rental, each month',
  'voice.apply.cost.note': 'Both are paid from credits you have bought. Credits that came with a plan cannot pay them.',
  'voice.apply.cost.topup': 'Buy credits',
  'voice.apply.notYet': 'Numbers cannot be applied for yet.',
  'voice.apply.kind.national': 'A national number (087)',
  'voice.apply.kind.geographic': 'A local number (010, 011, 021 and so on)',
  'voice.apply.status.submitted': 'Being reviewed',
  'voice.apply.status.approved': 'Approved',
  'voice.apply.status.rejected': 'Not approved',
  'voice.apply.status.withdrawn': 'Withdrawn',
  'voice.apply.line.submitted': 'Applied on {{when}}. {{fee}} is held until we decide; nothing has been charged.',
  'voice.apply.line.approved': 'Applied on {{when}}. {{fee}} was charged for registration.',
  'voice.apply.line.rejected': 'Applied on {{when}}. Nothing was charged.',
  'voice.apply.line.withdrawn': 'Applied on {{when}} and withdrawn. Nothing was charged.',
  'voice.apply.withdraw': 'Withdraw',
  'voice.apply.withdrawTitle': 'Withdraw this application?',
  'voice.apply.withdrawBody': 'The {{fee}} held for it becomes yours to spend again.',
  'voice.apply.withdrawn': 'Application withdrawn. The held credits are yours to spend again.',
  'voice.apply.sent': 'Application sent. We will review it and let you know.',
  'voice.apply.form.title': 'Apply for a number',
  'voice.apply.form.held': '{{fee}} is held when you apply and charged only if we approve. After that the rental is {{monthly}} a month. If we do not approve, or you withdraw, nothing is charged.',
  'voice.apply.form.kind': 'Kind of number',
  'voice.apply.form.kindHint.national': 'Not tied to a town. A good choice for a business that serves the whole country.',
  'voice.apply.form.kindHint.geographic': 'Carries a town\'s dialling code. We will ask which town when we review.',
  'voice.apply.form.business': 'Business name',
  'voice.apply.form.businessHint': 'The registered name the number is for.',
  'voice.apply.form.registration': 'Registration number',
  'voice.apply.form.registrationHint': 'Company or ID number, if you have one.',
  'voice.apply.form.contact': 'Contact person',
  'voice.apply.form.contactHint': 'Who we speak to about this application.',
  'voice.apply.form.phone': 'Contact phone',
  'voice.apply.form.phoneHint': 'A number we can reach that person on.',
  'voice.apply.form.address': 'Business address',
  'voice.apply.form.addressHint': 'Street address of the business.',
  'voice.apply.form.purpose': 'What the number is for',
  'voice.apply.form.purposeHint': 'For example: our shop\'s main line for customer questions.',
  'voice.apply.form.agree': 'I confirm these details are correct, and agree that {{fee}} is held now and charged if the application is approved.',
  'voice.apply.form.send': 'Send application',
  'voice.calls.recorded': 'Recorded',
  'voice.calls.quality.good': 'Clear line',
  'voice.calls.quality.fair': 'Line a little unclear',
  'voice.calls.quality.poor': 'Poor line',
  'voice.calls.detail.quality': 'Line',
  'voice.calls.recording.listen': 'Listen to the recording',
  'voice.calls.recording.listenHint': 'Each time a recording is opened it is noted, with who opened it.',
  'voice.calls.recording.keptUntil': 'This recording is kept until {{when}} and then deleted.',
  'voice.calls.recording.recording': 'This call is being recorded.',
  'voice.calls.recording.waiting': 'The recording is being saved. It will be here in a moment.',
  'voice.calls.recording.failed': 'The recording of this call could not be saved.',
  'voice.calls.recording.deleted': 'The recording of this call has been deleted. What was said is still below.',
  'voice.apply.form.documents': 'Documents',
  'voice.apply.form.documentsHint': 'The law (RICA) requires us to know who a number is for. Attach each document as a PDF or a photo; they go to your files and are only opened by the person who reviews your application.',
  'voice.apply.form.attach': 'Attach',
  'voice.apply.form.replace': 'Replace',
  'voice.apply.form.attachFor': 'Attach {{name}}',
  'voice.apply.form.attached': 'Attached: {{name}}',
  'voice.apply.form.notAttached': 'Not attached yet',
  'voice.apply.form.uploadFailed': 'The file could not be uploaded.',
  'voice.recording.noticeReady': 'Your notice is ready as speech. It is played before a call a person answers or places is recorded.',
  'voice.recording.noticeUnavailable': 'Your notice could not be made into speech yet, so calls a person answers or places are not recorded. Calls the assistant answers still are. Save it again later.',
  'voice.numbers.whatsapp': 'WhatsApp',
  'voice.numbers.whatsappHint': 'Takes calls from WhatsApp. Switch them off on the Numbers page.',
  'voice.calls.whatsapp': 'WhatsApp'
};

export default voice;
